import { CuppingSessionResultSampleScore } from '../types/cuppingResult'
import { CuppingSession, CuppingSessionSample } from '../types/cuppingSession'
import { CuppingScoreCalculation } from '../types/enums/cuppingScoreCalculation'
import { EvaluatorSampleScore } from '../types/evaluatorSampleScore'
import { Log } from '../types/log'
import { getLogName } from './logUtils'

export enum CuppingProgress {
  NotStarted = 1,
  InProgress = 2,
  Done = 3,
}

export const getEvaluators = (cuppingSession: CuppingSession): Array<string> => {
  return cuppingSession.session_scores ? cuppingSession.session_scores.map((x) => x.evaluator_name) : []
}

export const getCuppingSampleLabel = (index: number): string => {
  return `${index + 1}`
}

export const getCuppingSampleCoffeeName = (sample: CuppingSessionSample, batchString: string): string => {
  if (sample.log_data) {
    return getLogName(sample.log_data, '', batchString)
  }
  return sample.external_log_name || ''
}

export const createCuppingSessionSample = (logData: Log | null, externalLogName?: string): CuppingSessionSample => {
  return {
    log: logData?.url || null,
    log_data: logData || null,
    external_log_name: externalLogName || null,
  }
}

// create a new object for scores which aggregates scores per evaluator + sample
export const getEvaluatorScoresForSample = (
  session: CuppingSession,
  sampleId: number | undefined
): Array<EvaluatorSampleScore> => {
  const sample = session.cupping_samples.find((x) => x.id === sampleId)
  if (sample && session.session_scores) {
    return session.session_scores.map((score) => {
      const evaluatorScore = score.sample_scores.find((score) => score.cupping_session_sample === sample.id)
      return {
        id: evaluatorScore?.id,
        sampleId: sample.id,
        evaluator: score.evaluator_name,
        descriptors: evaluatorScore?.descriptors || [],
        notes: evaluatorScore?.notes || '',
        scores: evaluatorScore?.scores || [],
      }
    })
  }
  return []
}

export const averageArray = (arrayToReduce: Array<number>): number => {
  const average = arrayToReduce.reduce((aggr: number, current) => aggr + current, 0) / arrayToReduce.length
  return Math.round(average * 100) / 100
}

export const averageArrayString = (arrayToReduce: Array<number>): string => {
  const average = averageArray(arrayToReduce)
  return isNaN(average) ? 'N/A' : `${average}`
}

export const calculateSampleTotalScore = (session: CuppingSession, sampleId: number | undefined): string => {
  const totalScoresArray = getEvaluatorScoresForSample(session, sampleId).map((z: EvaluatorSampleScore) =>
    calculateSampleScore(session, z.scores)
  )
  return averageArrayString(totalScoresArray)
}

export const calculateSampleScore = (
  session: CuppingSession,
  sampleScores: Array<CuppingSessionResultSampleScore>
): number => {
  const scoreFieldIds = session.cupping_form_data.form_fields.filter((x) => x.included_in_score).map((x) => x.id)
  const scoreAggregator = sampleScores
    .filter((x) => (scoreFieldIds || []).indexOf(x.cupping_form_field) > -1)
    .map((x) => x.score)

  let score = 0
  if (session.cupping_form_data.score_calculation === CuppingScoreCalculation.Aggregated) {
    score = scoreAggregator.reduce((aggr: number, current) => aggr + (current || 0), 0)
  } else if (session.cupping_form_data.score_calculation === CuppingScoreCalculation.Average) {
    score = scoreAggregator.reduce((aggr: number, current) => aggr + (current || 0), 0) / scoreAggregator.length
  }
  const roundedScore = Math.round(score * 100) / 100
  return roundedScore + (session.cupping_form_data.score_constant || 0)
}
